/**
 * Tier Pricing Management — the inputs to the payment engine, and what they produce.
 *
 * Inputs: base price + plan payment count per tier, the two global fee percentages,
 * the maintenance fee, and one global drip-removal price. Everything else on this page
 * is derived by the backend engine and shown read-only.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Gift } from 'lucide-react';
import { API } from '@/App';
import PricingNotesCard from '@/components/admin/PricingNotesCard';

const PAID_TIERS = ['silver', 'gold', 'platinum'];

const tierAccent = {
  silver: 'border-gray-400/40',
  gold: 'border-yellow-500/40',
  platinum: 'border-purple-500/40',
};

const AdminTierPricing = () => {
  const [tiers, setTiers] = useState([]);
  const [table, setTable] = useState(null);
  const [fees, setFees] = useState({ processing_pct: 3, processing_fixed_cents: 30, convenience_pct: 10, drip_removal_price: 2500 });
  const [maintenanceFee, setMaintenanceFee] = useState(11100);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showCompDialog, setShowCompDialog] = useState(false);
  const [features, setFeatures] = useState({});
  const [badge, setBadge] = useState({ tier: '', label: 'Most chosen' });

  const saveBadge = async () => {
    setSaving('badge');
    try {
      await axios.put(`${API}/admin/tier-badge`, badge, auth());
      toast.success(badge.tier ? `Badge on ${badge.tier}` : 'Badge removed');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const saveFeatureList = async (tier) => {
    setSaving(tier);
    try {
      await axios.put(`${API}/admin/tier-features/${tier}`, {
        modules_label: features[tier]?.modules_label || '',
        forum_label: features[tier]?.forum_label || '',
        features: features[tier]?.features || [],
      }, auth());
      toast.success('List saved');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };
  const [users, setUsers] = useState([]);
  const [compForm, setCompForm] = useState({ user_id: '', tier: 'silver', remove_drip: false, reason: '' });

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    try {
      const [pricing, derived, feeSettings, maintenance, included] = await Promise.all([
        axios.get(`${API}/admin/tier-pricing`, auth()),
        axios.get(`${API}/membership/price-table`),
        axios.get(`${API}/admin/membership-fees`, auth()),
        axios.get(`${API}/admin/maintenance-fee`, auth()),
        axios.get(`${API}/admin/tier-features`, auth()),
      ]);
      setTiers(PAID_TIERS.map((tier) => pricing.data.find((t) => t.tier === tier)).filter(Boolean));
      setTable(derived.data);
      setFees(feeSettings.data);
      setMaintenanceFee(maintenance.data.amount);
      setFeatures(included.data.tiers);
      setBadge({ tier: included.data.badge || '', label: included.data.badge_label || 'Most chosen' });
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    axios.get(`${API}/admin/users?limit=1000`, auth())
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data.users || [];
        setUsers(list.filter((u) => u.status === 'active'));
      })
      .catch(() => {});
  }, [load]);

  const dollars = (cents) => (Number(cents || 0) / 100).toFixed(2);
  const toCents = (value) => Math.round(parseFloat(value || 0) * 100);
  const money = (value) => `$${Number(value || 0).toFixed(2)}`;

  const editTier = (tier, field, value) => {
    setTiers((prev) => prev.map((row) => (row.tier === tier ? { ...row, [field]: value } : row)));
  };

  const saveTier = async (row) => {
    setSaving(row.tier);
    try {
      await axios.put(`${API}/admin/tier-pricing/${row.tier}`, {
        tier: row.tier,
        monthly_price: row.monthly_price || 0,
        yearly_price: row.yearly_price,
        drip_removal_price: fees.drip_removal_price,
        plan_payments: Number(row.plan_payments || table?.tiers?.[row.tier]?.plan_count || 1),
      }, auth());
      await axios.put(`${API}/admin/tier-features/${row.tier}`, {
        modules_label: features[row.tier]?.modules_label || '',
        forum_label: features[row.tier]?.forum_label || '',
        features: (features[row.tier]?.features || []),
      }, auth());
      toast.success(`${row.tier.charAt(0).toUpperCase() + row.tier.slice(1)} saved`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const saveFees = async () => {
    setSaving('fees');
    try {
      await axios.put(`${API}/admin/membership-fees`, fees, auth());
      await axios.put(`${API}/admin/maintenance-fee`, { amount: maintenanceFee }, auth());
      toast.success('Global settings saved');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const compUser = async () => {
    try {
      await axios.post(`${API}/admin/comp-user`, compForm, auth());
      toast.success('Member comped');
      setShowCompDialog(false);
      setCompForm({ user_id: '', tier: 'silver', remove_drip: false, reason: '' });
    } catch (error) {
      toast.error('Failed to comp member');
    }
  };

  if (loading) return <div className="p-6 text-white">Loading tier pricing…</div>;

  return (
    <div className="p-6" data-testid="admin-tier-pricing">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Tier Pricing Management</h1>
          <p className="text-gray-300 text-sm">
            Set the base price and plan length. Everything billed is derived from these.
          </p>
        </div>
        <Button
          onClick={() => setShowCompDialog(true)}
          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
          data-testid="comp-user-btn"
        >
          <Gift className="w-4 h-4 mr-2" /> Comp Member
        </Button>
      </div>

      <div className="grid gap-6 max-w-5xl">
        {/* Global settings */}
        <Card className="border-2 border-cyan-400/30" data-testid="global-fees-card">
          <CardHeader>
            <h2 className="text-2xl font-bold text-gray-900">Global Settings</h2>
            <p className="text-sm text-gray-600">
              These apply across every tier. Processing ({'3% + $0.30'} by default) is charged on
              every payment; financing only on payment-plan instalments.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="processing-pct">Processing fee — percentage (every payment)</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="processing-pct" type="number" step="0.1"
                    value={fees.processing_pct}
                    onChange={(e) => setFees({ ...fees, processing_pct: parseFloat(e.target.value || 0) })}
                    data-testid="processing-pct-input"
                  />
                  <span className="text-gray-500">%</span>
                </div>
              </div>
              <div>
                <Label htmlFor="processing-fixed">Processing fee — flat amount (per payment)</Label>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-gray-900 font-bold">$</span>
                  <Input
                    id="processing-fixed" type="number" step="0.01"
                    value={dollars(fees.processing_fixed_cents)}
                    onChange={(e) => setFees({ ...fees, processing_fixed_cents: toCents(e.target.value) })}
                    data-testid="processing-fixed-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Stripe's real cost is {fees.processing_pct}% + {money(fees.processing_fixed_cents / 100)} per
                  charge. Charged once per payment — each plan instalment carries it.
                </p>
              </div>
              <div>
                <Label htmlFor="convenience-pct">Financing fee (plan instalments only)</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="convenience-pct" type="number" step="0.1"
                    value={fees.convenience_pct}
                    onChange={(e) => setFees({ ...fees, convenience_pct: parseFloat(e.target.value || 0) })}
                    data-testid="convenience-pct-input"
                  />
                  <span className="text-gray-500">%</span>
                </div>
              </div>
              <div>
                <Label htmlFor="maintenance-fee">Annual maintenance (base)</Label>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-gray-900 font-bold">$</span>
                  <Input
                    id="maintenance-fee" type="number" step="0.01"
                    value={dollars(maintenanceFee)}
                    onChange={(e) => setMaintenanceFee(toCents(e.target.value))}
                    data-testid="maintenance-fee-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Members are billed{' '}
                  {money((maintenanceFee / 100) * (1 + fees.processing_pct / 100) + fees.processing_fixed_cents / 100)} on
                  their anniversary, which never moves.
                </p>
              </div>
              <div>
                <Label htmlFor="drip-price">Drip removal add-on (one global price)</Label>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-gray-900 font-bold">$</span>
                  <Input
                    id="drip-price" type="number" step="0.01"
                    value={dollars(fees.drip_removal_price)}
                    onChange={(e) => setFees({ ...fees, drip_removal_price: toCents(e.target.value) })}
                    data-testid="drip-removal-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Billed{' '}
                  {money((fees.drip_removal_price / 100) * (1 + fees.processing_pct / 100) + fees.processing_fixed_cents / 100)}.
                  One-time, permanent, program-wide, never credited or refunded. Bronze has no drip
                  schedule (Module 1 only), so it is never offered there.
                </p>
              </div>
            </div>
            <Button
              onClick={saveFees} disabled={saving === 'fees'}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="save-global-fees-btn"
            >
              {saving === 'fees' ? 'Saving…' : 'Save Global Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* The comparison members see: badge and the Bronze column */}
        <Card className="border-2 border-amber-400/40" data-testid="tier-comparison-settings">
          <CardHeader>
            <h2 className="text-2xl font-bold text-gray-900">The Comparison Members See</h2>
            <p className="text-sm text-gray-600">
              Every gate in the app opens the same side-by-side comparison. Bronze appears as the
              "You're here" column, and one tier may wear a badge.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="badge-tier">Badge on which tier</Label>
                <select
                  id="badge-tier"
                  className="mt-2 w-full border rounded-md h-10 px-3 bg-white"
                  value={badge.tier}
                  onChange={(e) => setBadge({ ...badge, tier: e.target.value })}
                  data-testid="badge-tier-select"
                >
                  <option value="">No badge</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="platinum">Platinum</option>
                </select>
              </div>
              <div>
                <Label htmlFor="badge-label">What the badge says</Label>
                <Input
                  id="badge-label" className="mt-2"
                  value={badge.label}
                  onChange={(e) => setBadge({ ...badge, label: e.target.value })}
                  placeholder="Most chosen"
                  data-testid="badge-label-input"
                />
              </div>
            </div>
            <Button
              onClick={saveBadge} disabled={saving === 'badge'}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="save-badge-btn"
            >
              {saving === 'badge' ? 'Saving…' : 'Save Badge'}
            </Button>

            <div className="border-t pt-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="bronze-modules-label">Bronze — modules</Label>
                  <Input
                    id="bronze-modules-label" className="mt-2"
                    value={features.bronze?.modules_label || ''}
                    onChange={(e) => setFeatures((prev) => ({
                      ...prev, bronze: { ...prev.bronze, modules_label: e.target.value },
                    }))}
                    data-testid="tier-bronze-modules-label-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bronze-forum-label">Bronze — forum categories</Label>
                  <Input
                    id="bronze-forum-label" className="mt-2"
                    value={features.bronze?.forum_label || ''}
                    onChange={(e) => setFeatures((prev) => ({
                      ...prev, bronze: { ...prev.bronze, forum_label: e.target.value },
                    }))}
                    data-testid="tier-bronze-forum-label-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="bronze-features">Bronze — everything else members already have, one per line</Label>
                <Textarea
                  id="bronze-features" className="mt-2 font-mono text-sm" rows={6}
                  value={(features.bronze?.features || []).join('\n')}
                  onChange={(e) => setFeatures((prev) => ({
                    ...prev, bronze: { ...prev.bronze, features: e.target.value.split('\n') },
                  }))}
                  data-testid="tier-bronze-features-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Bronze has no price and nothing to buy — this column exists so members can see the
                  jump. Everything listed here is included in every paid tier too.
                </p>
              </div>
              <Button
                onClick={() => saveFeatureList('bronze')} disabled={saving === 'bronze'}
                variant="outline" className="w-full"
                data-testid="tier-bronze-save-btn"
              >
                {saving === 'bronze' ? 'Saving…' : 'Save Bronze column'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Per tier */}
        {tiers.map((row) => {
          const derived = table?.tiers?.[row.tier];
          return (
            <Card key={row.tier} className={`border-2 ${tierAccent[row.tier]}`} data-testid={`tier-card-${row.tier}`}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-900 capitalize">{row.tier}</h2>
                  <Badge className="bg-gray-900 text-white">{derived?.plan_count ?? '—'}-payment plan</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`${row.tier}-base`}>Base price</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-gray-900 font-bold">$</span>
                      <Input
                        id={`${row.tier}-base`} type="number" step="0.01"
                        value={dollars(row.yearly_price)}
                        onChange={(e) => editTier(row.tier, 'yearly_price', toCents(e.target.value))}
                        data-testid={`tier-${row.tier}-base-input`}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Before any fee or tax.</p>
                  </div>
                  <div>
                    <Label htmlFor={`${row.tier}-payments`}>Payments in the plan</Label>
                    <Input
                      id={`${row.tier}-payments`} type="number" min="1" step="1"
                      className="mt-2"
                      value={row.plan_payments ?? derived?.plan_count ?? ''}
                      onChange={(e) => editTier(row.tier, 'plan_payments', e.target.value)}
                      data-testid={`tier-${row.tier}-payments-input`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Mirrors the coaching rhythm. One a month, the first at checkout.
                    </p>
                  </div>
                </div>

                <div className="rounded-lg bg-gray-100 border border-gray-200 p-4 text-sm" data-testid={`tier-${row.tier}-derived`}>
                  <p className="text-cyan-700 font-semibold mb-2">Derived — read only</p>
                  <div className="grid sm:grid-cols-3 gap-3 text-gray-900">
                    <div>
                      <span className="block text-xs text-gray-500">Pay in full</span>
                      <span className="text-lg font-semibold">{money(derived?.pay_in_full)}</span>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500">Each instalment</span>
                      <span className="text-lg font-semibold">
                        {money(derived?.payments?.[0]?.billed)}
                        {derived?.payments?.length > 1 &&
                          derived.payments[derived.payments.length - 1].billed !== derived.payments[0].billed && (
                            <span className="text-xs text-gray-500">
                              {' '}(final {money(derived.payments[derived.payments.length - 1].billed)})
                            </span>
                          )}
                      </span>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500">Plan total</span>
                      <span className="text-lg font-semibold">{money(derived?.plan_total)}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3" data-testid={`tier-${row.tier}-features`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`${row.tier}-modules-label`}>Modules this tier opens</Label>
                      <Input
                        id={`${row.tier}-modules-label`} className="mt-2"
                        placeholder="Modules 1-4"
                        value={features[row.tier]?.modules_label || ''}
                        onChange={(e) => setFeatures((prev) => ({
                          ...prev,
                          [row.tier]: { ...prev[row.tier], modules_label: e.target.value },
                        }))}
                        data-testid={`tier-${row.tier}-modules-label-input`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`${row.tier}-forum-label`}>Forum categories this tier opens</Label>
                      <Input
                        id={`${row.tier}-forum-label`} className="mt-2"
                        placeholder="Categories 1-4"
                        value={features[row.tier]?.forum_label || ''}
                        onChange={(e) => setFeatures((prev) => ({
                          ...prev,
                          [row.tier]: { ...prev[row.tier], forum_label: e.target.value },
                        }))}
                        data-testid={`tier-${row.tier}-forum-label-input`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`${row.tier}-features`}>Everything else this tier adds — one per line</Label>
                    <Textarea
                      id={`${row.tier}-features`} className="mt-2 font-mono text-sm" rows={6}
                      value={(features[row.tier]?.features || []).join('\n')}
                      onChange={(e) => setFeatures((prev) => ({
                        ...prev,
                        [row.tier]: { ...prev[row.tier], features: e.target.value.split('\n') },
                      }))}
                      data-testid={`tier-${row.tier}-features-input`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Each tier already includes everything in the tier below it, so list only what
                      this tier <strong>adds</strong>. Modules and forum categories have their own
                      boxes above — leave them out of this list. Blank lines are ignored.
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => saveTier(row)} disabled={saving === row.tier}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid={`tier-${row.tier}-save-btn`}
                >
                  {saving === row.tier ? 'Saving…' : `Save ${row.tier}`}
                </Button>
              </CardContent>
            </Card>
          );
        })}

        <PricingNotesCard table={table} />
      </div>

      <Dialog open={showCompDialog} onOpenChange={setShowCompDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Comp a Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Member</Label>
              <Select value={compForm.user_id} onValueChange={(v) => setCompForm({ ...compForm, user_id: v })}>
                <SelectTrigger data-testid="comp-user-select"><SelectValue placeholder="Choose a member" /></SelectTrigger>
                <SelectContent>
                  {users.slice(0, 300).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tier</Label>
              <Select value={compForm.tier} onValueChange={(v) => setCompForm({ ...compForm, tier: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAID_TIERS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  <SelectItem value="free_comp">Free-Comp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea
                value={compForm.reason}
                onChange={(e) => setCompForm({ ...compForm, reason: e.target.value })}
                placeholder="Why is this being comped?"
              />
            </div>
            <Button onClick={compUser} disabled={!compForm.user_id} className="w-full" data-testid="comp-user-submit">
              Comp Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTierPricing;
